import React, { useState, useEffect, useCallback } from "react";
import { useDispatch, useSelector, shallowEqual } from "react-redux";
import * as auth from "../Auth/_redux/authRedux";
import { getOnboardingStatus } from "../../UrlCrud";
import WelcomePath from "./WelcomePath";
import IndividualOnboarding from "./IndividualOnboarding";
import OrganisationOnboarding from "./OrganisationOnboarding";
import "./onboarding.scss";

const STEP_LABELS = {
    organisation: "Organisation Information",
    verification: "Registration / Verification Information",
    contact: "Primary Contact / Administrator",
    documents: "Supporting Documents",
    individual: "Profile Information",
};

const STEP_INDEX = { organisation: 0, verification: 1, contact: 2, documents: 3 };

// The single restricted-account experience - covers every non-approved
// account state (see src/middleware/onboarding.js's ACCOUNT_STATUS on the
// backend for the matching source of truth). Deliberately ONE component
// with a state-driven switch, rather than scattering "if not approved"
// checks across the app - this is the ONE place that decides what a
// pending/reviewing/changes-required/rejected/suspended user sees.
export default function AccountWorkspace() {
    const dispatch = useDispatch();
    const { currentUser } = useSelector(
        ({ auth }) => ({ currentUser: auth.user && auth.user.user }),
        shallowEqual
    );

    const [status, setStatus] = useState(null);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState("status");

    const refresh = useCallback(() => {
        return getOnboardingStatus()
            .then(({ data }) => setStatus(data))
            .catch(() => setStatus(null))
            .finally(() => setLoading(false));
    }, []);

    useEffect(() => {
        refresh();
    }, [refresh]);

    if (loading || !status) {
        return (
            <div className="onboarding-shell">
                <div className="onboarding-card text-center text-muted">Loading your account status...</div>
            </div>
        );
    }

    const { accountStatus, onboardingType, checklist, changesRequested, company, documentRequirements } = status;

    const accountSetupTabs = [
        { key: "status", label: "Status" },
        ...(onboardingType === "organisation" && company ? [{ key: "organisation", label: "Organisation" }] : []),
        { key: "profile", label: "Profile" },
    ];

    const renderAccountSetupNav = () => (
        <div className="onboarding-tabs mb-6">
            {accountSetupTabs.map(tab => (
                <button
                    key={tab.key}
                    type="button"
                    className={`onboarding-tabs__item ${activeTab === tab.key ? "onboarding-tabs__item--active" : ""}`}
                    onClick={() => setActiveTab(tab.key)}
                >
                    {tab.label}
                </button>
            ))}
        </div>
    );

    // Read-only - the actual editable version of this data lives in the
    // onboarding wizard itself (only reachable pre-submission); once
    // pending/rejected/suspended, this just lets the user see what they
    // already submitted, using data the backend already returns here.
    const renderOrganisationPanel = () => (
        <div>
            <h5 className="font-weight-bolder mb-4">{company.name}</h5>
            <div className="mb-2"><span className="text-muted">Address: </span>{company.address || "Not provided"}</div>
            <div className="mb-2"><span className="text-muted">Email: </span>{company.email || "Not provided"}</div>
            <div className="mb-4"><span className="text-muted">Verification status: </span>{company.verificationStatus || "pending"}</div>
            {Array.isArray(company.documents) && company.documents.length > 0 && (
                <>
                    <h6 className="font-weight-bolder mb-2">Submitted documents</h6>
                    {company.documents.map((doc, idx) => (
                        <div key={idx} className="mb-1">{doc.type || "Document"} - <span className="text-muted">{doc.status || "pending"}</span></div>
                    ))}
                </>
            )}
        </div>
    );

    // Read-only. NOTE: PUT /v2/user/edit/:id (the only existing user-update
    // endpoint) requires the `manageUser` permission, which a pending
    // account never has (no `userRole` is assigned until approval) - there
    // is no functional self-service profile edit for this account state on
    // the backend today, so this deliberately does not offer one rather
    // than wiring up a save button that would always 401.
    const renderProfilePanel = () => (
        <div>
            <div className="mb-2"><span className="text-muted">Email: </span>{currentUser.email}</div>
            <div className="mb-2"><span className="text-muted">First name: </span>{currentUser.firstName}</div>
            <div className="mb-2"><span className="text-muted">Last name: </span>{currentUser.lastName}</div>
            <div className="mb-4"><span className="text-muted">Telephone: </span>{currentUser.telephone || "Not provided"}</div>
        </div>
    );

    const renderChecklist = () => (
        <div className="onboarding-checklist mb-6">
            {checklist.map(item => (
                <div
                    key={item.key}
                    className={`onboarding-checklist__item ${
                        item.completed ? "onboarding-checklist__item--done" : item.current ? "onboarding-checklist__item--current" : "onboarding-checklist__item--pending"
                    }`}
                >
                    <span className="onboarding-checklist__item__icon">
                        {item.completed ? "\u2713" : item.current ? "\u25cb" : "\u2022"}
                    </span>
                    <span>{item.label}</span>
                </div>
            ))}
        </div>
    );

    // Note: `pending_email_verification` can't actually reach this
    // component - login itself is blocked server-side until emailVerified
    // is true (see the login handler in src/controller/account.js), so any
    // authenticated session here has already verified its email.

    if (accountStatus === "onboarding" && !onboardingType) {
        return (
            <div className="onboarding-shell">
                <WelcomePath user={currentUser} onChoose={() => refresh()} />
            </div>
        );
    }

    if (accountStatus === "onboarding" || accountStatus === "changes_required") {
        const initialStep = changesRequested && STEP_INDEX[changesRequested.step] !== undefined ? STEP_INDEX[changesRequested.step] : 0;
        return (
            <div className="onboarding-shell">
                {accountStatus === "changes_required" && (
                    <div className="onboarding-card mb-4" style={{ borderLeft: "4px solid #ffa800" }}>
                        <h5 className="font-weight-bolder mb-2">Changes requested</h5>
                        {changesRequested && changesRequested.step && (
                            <p className="mb-2">
                                Please revisit: <strong>{STEP_LABELS[changesRequested.step] || changesRequested.step}</strong>
                            </p>
                        )}
                        <p className="text-muted mb-0">{(changesRequested && changesRequested.reason) || "A Super Admin has requested changes to your submission."}</p>
                    </div>
                )}
                {onboardingType === "individual" ? (
                    <IndividualOnboarding user={currentUser} onSubmitted={refresh} onRefresh={refresh} />
                ) : (
                    <OrganisationOnboarding
                        user={currentUser}
                        company={company}
                        documentRequirements={documentRequirements}
                        onSubmitted={refresh}
                        onRefresh={refresh}
                        initialStep={initialStep}
                    />
                )}
            </div>
        );
    }

    if (accountStatus === "pending_review") {
        return (
            <div className="onboarding-shell">
                <div className="onboarding-card">
                    {renderAccountSetupNav()}
                    {activeTab === "organisation" ? (
                        renderOrganisationPanel()
                    ) : activeTab === "profile" ? (
                        renderProfilePanel()
                    ) : (
                        <>
                            <h4 className="font-weight-bolder mb-2">Your account is under review</h4>
                            <p className="text-muted mb-6">
                                {onboardingType === "organisation"
                                    ? "Your email has been verified and your organisation information has been submitted."
                                    : "Your email has been verified and your information has been submitted."}
                            </p>
                            {renderChecklist()}
                            <p className="text-muted mt-6 mb-0">
                                A Super Admin is reviewing your account. You'll be notified once a decision is made - no action is needed from you right now.
                            </p>
                        </>
                    )}
                    <div className="onboarding-footer-actions">
                        <button className="btn btn-light-primary" onClick={() => dispatch(auth.actions.logout())}>Log out</button>
                    </div>
                </div>
            </div>
        );
    }

    if (accountStatus === "rejected") {
        return (
            <div className="onboarding-shell">
                <div className="onboarding-card">
                    {renderAccountSetupNav()}
                    {activeTab === "organisation" ? (
                        renderOrganisationPanel()
                    ) : activeTab === "profile" ? (
                        renderProfilePanel()
                    ) : (
                        <>
                            <h4 className="font-weight-bolder mb-2">Your registration was not approved</h4>
                            <p className="text-muted mb-6">
                                {(status.reviewHistory && status.reviewHistory.length > 0 && status.reviewHistory[status.reviewHistory.length - 1].reason) ||
                                    "Please contact support for more information."}
                            </p>
                        </>
                    )}
                    <div className="onboarding-footer-actions">
                        <button className="btn btn-light-primary" onClick={() => dispatch(auth.actions.logout())}>Log out</button>
                    </div>
                </div>
            </div>
        );
    }

    if (accountStatus === "suspended") {
        return (
            <div className="onboarding-shell">
                <div className="onboarding-card">
                    {renderAccountSetupNav()}
                    {activeTab === "organisation" ? (
                        renderOrganisationPanel()
                    ) : activeTab === "profile" ? (
                        renderProfilePanel()
                    ) : (
                        <>
                            <h4 className="font-weight-bolder mb-2">Your account has been suspended</h4>
                            <p className="text-muted mb-6">
                                {(status.reviewHistory && status.reviewHistory.length > 0 && status.reviewHistory[status.reviewHistory.length - 1].reason) ||
                                    "Please contact support for more information."}
                            </p>
                        </>
                    )}
                    <div className="onboarding-footer-actions">
                        <button className="btn btn-light-primary" onClick={() => dispatch(auth.actions.logout())}>Log out</button>
                    </div>
                </div>
            </div>
        );
    }

    return null;
}
